import {
  ApiClient,
  ApiClientParams,
  ApiClientResponse,
  Api500Error,
  Api403Error,
  Api404Error,
  Api405Error,
  Api409Error,
  Api410Error
} from '@/Domain/ApiClient'
import axios, { Method, AxiosRequestConfig } from 'axios'
import { TokenStorage } from '@/Domain/Account/TokenStorage'

const axiosInstance = axios.create({})

export class AxiosApiClient implements ApiClient {
  private _tokenStorage: TokenStorage

  constructor (tokenStorage: TokenStorage) {
    this._tokenStorage = tokenStorage
  }

  makeGetRequest (url: string, queryParams: ApiClientParams = {}): Promise<ApiClientResponse> {
    const axiosRequestConfig = {
      url: url,
      params: queryParams
    }
    return this.makeRequest(axiosRequestConfig)
  }

  makePostRequest (url: string, params: ApiClientParams): Promise<ApiClientResponse> {
    const axiosRequestConfig = {
      method: 'POST' as Method,
      url: url,
      data: params
    }
    return this.makeRequest(axiosRequestConfig)
  }

  makePatchRequest (url: string, params: ApiClientParams): Promise<ApiClientResponse> {
    const axiosRequestConfig = {
      method: 'PATCH' as Method,
      url: url,
      data: params
    }
    return this.makeRequest(axiosRequestConfig)
  }

  makeDeleteRequest (url: string): Promise<ApiClientResponse> {
    const axiosRequestConfig = {
      method: 'DELETE' as Method,
      url: url
    }
    return this.makeRequest(axiosRequestConfig)
  }

  private async makeRequest (config: AxiosRequestConfig): Promise<ApiClientResponse> {
    config.baseURL = process.env.VUE_APP_API_URL
    if (config.headers === undefined) {
      config.headers = {}
    }
    config.headers.Authorization = 'Bearer ' + this._tokenStorage.getToken()
    try {
      const response = await axiosInstance(config)
      return Promise.resolve(response)
    } catch (error) {
      if (error.response) {
        const message = error.response.data.message || error.message
        this.parseAxiosError(error.response.status, message)
      }
      throw new Error(`Undefined Axios error (${error.message})`)
    }
  }

  private parseAxiosError (status: number, message: string): never {
    if (status === 409) {
      throw new Api409Error(message)
    }
    if (status === 403) {
      throw new Api403Error(message)
    }
    if (status === 404) {
      throw new Api404Error(message)
    }
    if (status === 405) {
      throw new Api405Error(message)
    }
    if (status === 410) {
      throw new Api410Error(message)
    }
    throw new Api500Error(message)
  }
}
